import { useCallback, useEffect, useState, type RefObject } from "react"

/**
 * Фулскрин шелла плеера. Где Fullscreen API недоступен (iPhone Safari) — сначала
 * пробуем нативный системный видеоплеер (requestNativeVideoFullscreen), и только
 * потом псевдо-фулскрин: шелл разворачивается на весь экран fixed-оверлеем,
 * а Escape перехватывается раньше слушателей Radix, чтобы выход из фулскрина
 * не закрывал диалог позади плеера.
 */
export function useShellFullscreen(
  shellRef: RefObject<HTMLElement | null>,
  /** iPhone-фолбэк: открыть системный фулскрин видеоэлемента; true — открылся. */
  requestNativeVideoFullscreen?: () => boolean,
): {
  /** Активен любой из режимов — нативный или псевдо. */
  isFullscreen: boolean
  /** Активен CSS-фолбэк: шеллу нужны классы fixed-оверлея. */
  isPseudoFullscreen: boolean
  toggleFullscreen: () => Promise<void>
} {
  const [isNative, setIsNative] = useState(false)
  const [isPseudo, setIsPseudo] = useState(false)

  useEffect(() => {
    const onFs = () => setIsNative(document.fullscreenElement === shellRef.current)
    document.addEventListener("fullscreenchange", onFs)
    return () => document.removeEventListener("fullscreenchange", onFs)
  }, [shellRef])

  // Escape в фулскрине не должен доходить до диалога позади плеера: capture на
  // window срабатывает раньше document-слушателей Radix (DismissableLayer).
  useEffect(() => {
    if (!isNative && !isPseudo) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      e.stopPropagation()
      if (isPseudo) {
        e.preventDefault()
        setIsPseudo(false)
      }
      // Из нативного режима браузер выходит по Escape сам.
    }
    window.addEventListener("keydown", onKeyDown, { capture: true })
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true })
  }, [isNative, isPseudo])

  // Псевдо-режим: страница позади скроллиться не должна.
  useEffect(() => {
    if (!isPseudo) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [isPseudo])

  // Псевдо-режим: через Popover API шелл поднимается в top layer — там fixed-оверлей
  // позиционируется от вьюпорта даже внутри transform-предка (модалки центрируются
  // translate'ом, который иначе делал бы их containing block'ом для fixed).
  // Без поддержки popover остаётся обычный fixed-фолбэк.
  useEffect(() => {
    if (!isPseudo) return
    const shell = shellRef.current
    if (!shell || typeof shell.showPopover !== "function") return
    shell.setAttribute("popover", "manual")
    try {
      shell.showPopover()
    } catch {
      // Невалидное состояние элемента — остаёмся на fixed-фолбэке.
    }
    return () => {
      try {
        shell.hidePopover()
      } catch {
        // Уже скрыт (например, элемент удалён из DOM).
      }
      shell.removeAttribute("popover")
    }
  }, [isPseudo, shellRef])

  const toggleFullscreen = useCallback(async () => {
    const shell = shellRef.current
    if (!shell) return
    if (document.fullscreenElement === shell) {
      try {
        await document.exitFullscreen()
      } catch {
        // Браузер мог уже выйти сам — игнорируем.
      }
      return
    }
    if (isPseudo) {
      setIsPseudo(false)
      return
    }
    const canNative = typeof shell.requestFullscreen === "function" && document.fullscreenEnabled !== false
    if (canNative) {
      try {
        if (document.fullscreenElement) await document.exitFullscreen()
        await shell.requestFullscreen()
        return
      } catch {
        // Запрос отклонён (например, iframe без allowfullscreen) — фолбэки ниже.
      }
    }
    // iPhone Safari: элементного Fullscreen API нет вовсе — системный плеер видео.
    // Состояние здесь не трекаем: движок сам вернёт управление по webkitendfullscreen.
    if (requestNativeVideoFullscreen?.()) return
    setIsPseudo(true)
  }, [shellRef, isPseudo, requestNativeVideoFullscreen])

  return { isFullscreen: isNative || isPseudo, isPseudoFullscreen: isPseudo, toggleFullscreen }
}
